/**
 * Non-lus annoncés mais pas encore visibles (préparation corps).
 * Pastilles sidebar = snapshot − hold.
 *
 * Règle : ne jamais écraser le hold d’un autre compte
 * (sinon pastille fantôme pendant qu’un compte est en prep).
 */
const holdByAccount = new Map<number, number>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((cb) => cb());
}

export function subscribeUnreadHold(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUnreadHold(accountId: number): number {
  return Math.max(0, holdByAccount.get(accountId) ?? 0);
}

export function getTotalUnreadHold(): number {
  let sum = 0;
  for (const n of holdByAccount.values()) sum += Math.max(0, n);
  return sum;
}

function setHold(accountId: number, count: number): boolean {
  const next = Math.max(0, count);
  const prev = holdByAccount.get(accountId) ?? 0;
  if (next === prev) return false;
  if (next === 0) holdByAccount.delete(accountId);
  else holdByAccount.set(accountId, next);
  return true;
}

/**
 * Aligne les holds des comptes **en cours de préparation dans la vue**.
 * `leavingAccountIds` = comptes qui venaient de prep dans cette vue et n’y sont plus.
 * Les autres comptes (sync fond) ne sont pas touchés.
 * Appeler avec count = max(préparant vue, pending reveal unread) pour ne pas sous-compter.
 */
export function syncUnreadHoldsFromPreparing(
  byAccount: ReadonlyMap<number, number>,
  leavingAccountIds: ReadonlySet<number> = new Set()
): void {
  let changed = false;

  for (const [accountId, count] of byAccount) {
    if (accountId < 1) continue;
    if (setHold(accountId, count)) changed = true;
  }

  for (const accountId of leavingAccountIds) {
    if (accountId < 1) continue;
    if (byAccount.has(accountId)) continue;
    if (setHold(accountId, 0)) changed = true;
  }

  if (changed) notify();
}

/**
 * Pose le hold à une valeur absolue (pending reveal / prep).
 */
export function setUnreadHoldCount(accountId: number, count: number): void {
  if (accountId < 1) return;
  if (setHold(accountId, count)) notify();
}

/** Libère le hold d’un compte (mail déjà visible / pas de prep). */
export function clearUnreadHoldForAccount(accountId: number): void {
  if (accountId < 1) return;
  if (setHold(accountId, 0)) notify();
}
